import type { CarSpec } from './types';
import { RADS_TO_RPM, RPM_TO_RADS, clamp, interp1, smoothstep, mulberry32 } from './math';

/**
 * Engine, turbo/anti-lag, sequential gearbox, auto-clutch and the 4WD driveline.
 *
 * Driveline layout (Rally2): engine → clutch → 5-speed sequential → front LSD, and a rigid shaft
 * from the gearbox to the rear LSD (no centre differential). Pulling the hydraulic handbrake opens
 * a clutch in that shaft so the rear axle is disconnected while the fronts keep driving.
 *
 * The rotational system has 5 degrees of freedom: engine, FL, FR, RL, RR. Couplings are solved
 * as velocity constraints with torque limits (projected Gauss-Seidel, warm-started):
 *   clutch   ω_e − G·(ω_FL+ω_FR)/2 = 0        |τ| ≤ clutch capacity
 *   centre   (ω_FL+ω_FR)/2 − (ω_RL+ω_RR)/2 = 0 |τ| ≤ ∞ (or the handbrake-disconnect clutch limit)
 *   front LSD ω_FL − ω_FR = 0                  |τ| ≤ preload + ramp·|τ_in|
 *   rear LSD  ω_RL − ω_RR = 0                  |τ| ≤ preload + ramp·|τ_in|
 *   brake_i  ω_i = 0                           |τ| ≤ brake torque + rolling resistance torque
 * Open-differential torque splitting falls out of the Jacobians (equal ½ weights).
 */

const NB = 5; // bodies: 0 engine, 1 FL, 2 FR, 3 RL, 4 RR
const NC = 8; // constraints

export interface DrivelineOutputs {
  clutchTorque: number;
  centreTorque: number;
  frontDiffTorque: number;
  rearDiffTorque: number;
}

export class Drivetrain {
  readonly spec: CarSpec;
  // Initialised (not just declared): a class field starts as undefined, and V8 then keeps a field that
  // later holds doubles tagged, so every write would box a new number (240 times a second here).
  engineOmega = 0;
  gear = 1;
  targetGear = 1;
  shiftTimer = 0;
  shiftCooldown = 0;
  boost = 0;
  throttleEff = 0;
  limiterCut = false;
  clutchEngage = 0;
  clutchLocked = false;
  /** Car held at rest on the brakes with throttle: clutch out, launch rev limiter (start-line launch). */
  launchHold = false;
  engineTorque = 0;
  /** Anti-lag pop/bang events emitted (monotonic counter; audio/visuals diff it). */
  popCount = 0;
  popIntensity = 0;
  private popTimer = 0;
  private alsBurst = 0;
  private lastThrottle = 0;
  private rng: () => number;
  reverseHold = 0;
  inReverse = false;
  antiLag = true;
  /** For TC: engine torque multiplier requested by the traction-control assist (0..1). */
  tcFactor = 1;

  // PGS state
  private invI = new Float64Array(NB);
  readonly omega = new Float64Array(NB);
  private J = new Float64Array(NC * NB);
  private lo = new Float64Array(NC);
  private hi = new Float64Array(NC);
  private lambda = new Float64Array(NC);
  private active = new Uint8Array(NC);
  readonly out: DrivelineOutputs = { clutchTorque: 0, centreTorque: 0, frontDiffTorque: 0, rearDiffTorque: 0 };

  constructor(spec: CarSpec, seed = 1) {
    this.spec = spec;
    this.engineOmega = spec.engine.idleRpm * RPM_TO_RADS;
    this.rng = mulberry32(seed ^ 0x9e3779b9);
  }

  get rpm(): number {
    return this.engineOmega * RADS_TO_RPM;
  }

  /** Overall ratio for a gear index (−1 = reverse, 0 = neutral). */
  ratio(gear: number): number {
    const g = this.spec.gearbox;
    if (gear === 0) return 0;
    if (gear < 0) return -g.reverse * g.finalDrive;
    return g.ratios[gear - 1] * g.finalDrive;
  }

  get topGear(): number {
    return this.spec.gearbox.ratios.length;
  }

  get shifting(): boolean {
    return this.shiftTimer > 0;
  }

  requestShift(to: number): void {
    if (this.shiftTimer > 0) return;
    const t = clamp(to, -1, this.topGear);
    if (t === this.gear) return;
    this.targetGear = t;
    this.shiftTimer = this.spec.gearbox.shiftTime;
    this.shiftCooldown = this.spec.gearbox.shiftTime + 0.25;
    // A flat upshift with ignition cut on an anti-lag engine produces a bang.
    if (t > this.gear && this.gear > 0 && this.rpm > 4500) {
      this.popCount++;
      this.popIntensity = 1;
    }
  }

  /** Engine torque (N·m) at the crank for the current state, before the clutch. */
  private computeEngineTorque(dt: number, throttleIn: number): number {
    const e = this.spec.engine;
    const rpm = this.rpm;
    // Idle governor keeps the engine alive (the auto-clutch means it can never stall).
    const idleThrottle = clamp((e.idleRpm * 1.03 - rpm) / 150, 0, 1) * 0.6;
    let thr = Math.max(throttleIn, idleThrottle);

    // Gear change: ignition cut on upshift, auto-blip on downshift.
    if (this.shiftTimer > 0) {
      if (this.targetGear > this.gear || this.targetGear <= 0) thr = 0;
      else thr = 0.85;
    }
    // Rev limiter (hard cut with hysteresis); a lower launch limiter while held on the brakes.
    const limit = this.launchHold ? this.spec.clutch.launchRpm + 600 : e.limiterRpm;
    if (rpm > limit) this.limiterCut = true;
    else if (rpm < limit - 180) this.limiterCut = false;
    if (this.limiterCut) thr = 0;
    thr *= this.tcFactor;

    // Turbo spool with lag; anti-lag holds boost off-throttle.
    const flow = smoothstep(e.turbo.boostStartRpm, e.turbo.boostFullRpm, rpm);
    let target = thr * flow;
    const alsOn = this.antiLag && thr < 0.15 && rpm > e.turbo.antiLagMinRpm && this.gear !== 0;
    if (alsOn) target = Math.max(target, e.turbo.antiLagBoost);
    const tau = target > this.boost ? e.turbo.spoolUpTime / (0.35 + 0.65 * flow) : e.turbo.spoolDownTime;
    this.boost += (target - this.boost) * (1 - Math.exp(-dt / tau));

    // Anti-lag bursts: lifting off from high rpm fires a run of pops while the ALS is active.
    if (this.lastThrottle > 0.5 && throttleIn < 0.15 && rpm > e.turbo.antiLagMinRpm && this.antiLag) {
      this.alsBurst = 0.35 + 0.6 * this.rng();
    }
    this.lastThrottle = throttleIn;
    if (this.alsBurst > 0 && alsOn) {
      this.alsBurst -= dt;
      this.popTimer -= dt;
      if (this.popTimer <= 0) {
        this.popCount++;
        this.popIntensity = 0.4 + 0.6 * this.rng();
        this.popTimer = 0.04 + 0.12 * this.rng();
      }
    } else {
      this.alsBurst = 0;
    }

    this.throttleEff = thr;
    const full = interp1(e.torqueCurve, rpm);
    const boostMul = e.naTorqueFraction + (1 - e.naTorqueFraction) * this.boost;
    // The torque curve is brake torque (net of internal friction), so friction and pumping losses
    // only appear as the throttle closes; driveline losses scale the delivered torque.
    const drive = thr * full * boostMul * this.spec.gearbox.efficiency;
    const friction = e.frictionTorque[0] + (e.frictionTorque[1] * rpm) / 1000;
    const pumping = e.engineBrakeTorque * clamp(rpm / e.limiterRpm, 0, 1.2);
    return drive - (1 - thr) * (friction + pumping);
  }

  /**
   * Advance the gearbox timers and auto-clutch, then solve the driveline for one step.
   * @param wheelOmega in/out wheel angular velocities (FL, FR, RL, RR)
   * @param wheelInertia effective inertias (wheel + implicit tyre stiffness term)
   * @param wheelTorque external torque on each wheel this step (tyre reaction etc.)
   * @param brakeLimit per-wheel torque limit for brake/rolling-resistance lock (N·m, ≥ 0)
   * @param handbrake 0..1 — opens the rear-drive disconnect clutch
   */
  step(
    dt: number,
    throttle: number,
    handbrake: number,
    wheelOmega: Float64Array,
    wheelInertia: Float64Array,
    wheelTorque: Float64Array,
    brakeLimit: Float64Array,
  ): void {
    const spec = this.spec;
    // Gear change completes after the shift time.
    if (this.shiftTimer > 0) {
      this.shiftTimer -= dt;
      if (this.shiftTimer <= 0) {
        this.shiftTimer = 0;
        this.gear = this.targetGear;
      }
    }
    if (this.shiftCooldown > 0) this.shiftCooldown -= dt;
    this.popIntensity *= Math.exp(-dt / 0.08);

    const Te = this.computeEngineTorque(dt, throttle);
    this.engineTorque = Te;

    const G = this.shiftTimer > 0 ? 0 : this.ratio(this.gear);
    // Auto-clutch: engagement follows engine speed so launches slip at a throttle-dependent
    // rpm (the engine can rev into boost) and the engine never stalls; once the wheels drive
    // the engine above engageRpm the clutch is fully locked.
    const c = spec.clutch;
    const slipLo = spec.engine.idleRpm + 100 + (c.launchRpm - spec.engine.idleRpm - 600) * clamp(throttle, 0, 1);
    const engineEng = smoothstep(slipLo, slipLo + 1200, this.rpm);
    const axle = 0.5 * (wheelOmega[0] + wheelOmega[1]);
    const wheelRpm = Math.abs(G * axle) * RADS_TO_RPM;
    // Lock once the wheels have caught up with the engine (no bogging out of boost); unlock only
    // when the wheel-driven engine speed falls toward idle (stall prevention) or in neutral.
    if (this.clutchLocked) {
      if (G === 0 || wheelRpm < c.engageRpm) this.clutchLocked = false;
    } else if (G !== 0 && wheelRpm > c.fullRpm && wheelRpm > this.rpm - 250) this.clutchLocked = true;
    const targetEng = G === 0 || this.launchHold ? 0 : this.clutchLocked ? 1 : engineEng;
    // Engage smoothly; release quickly (stall protection when the wheels slow under braking).
    const clutchTau = targetEng < this.clutchEngage ? 0.02 : 0.05;
    this.clutchEngage += (targetEng - this.clutchEngage) * (1 - Math.exp(-dt / clutchTau));

    // Bodies.
    const om = this.omega;
    const invI = this.invI;
    om[0] = this.engineOmega;
    invI[0] = 1 / spec.engine.inertia;
    for (let i = 0; i < 4; i++) {
      om[i + 1] = wheelOmega[i];
      invI[i + 1] = 1 / wheelInertia[i];
    }
    // External torques → unconstrained velocities.
    om[0] += dt * Te * invI[0];
    for (let i = 0; i < 4; i++) om[i + 1] += dt * wheelTorque[i] * invI[i + 1];

    // Constraint setup.
    const J = this.J;
    J.fill(0);
    const lo = this.lo;
    const hi = this.hi;
    const act = this.active;
    // 0: clutch
    const clutchCap = this.clutchEngage * c.maxTorque * dt;
    act[0] = G !== 0 && clutchCap > 0 ? 1 : 0;
    J[0 * NB + 0] = 1;
    J[0 * NB + 1] = -G * 0.5;
    J[0 * NB + 2] = -G * 0.5;
    lo[0] = -clutchCap;
    hi[0] = clutchCap;
    // 1: centre coupling (rigid, opened by the handbrake disconnect clutch)
    const hb = clamp(handbrake, 0, 1);
    const centreCap = hb > 0.05 ? (1 - smoothstep(0.05, 0.35, hb)) * 2000 * dt : 1e9;
    act[1] = 1;
    J[1 * NB + 1] = 0.5;
    J[1 * NB + 2] = 0.5;
    J[1 * NB + 3] = -0.5;
    J[1 * NB + 4] = -0.5;
    lo[1] = -centreCap;
    hi[1] = centreCap;
    // 2/3: LSDs — lock torque from previous step's input torques
    const tauClutch = this.lambda[0] / dt;
    const tauCentre = this.lambda[1] / dt;
    // Clutch impulse acts −G/2 on each front wheel; centre impulse +½ on fronts, −½ on rears.
    const frontIn = -G * tauClutch + tauCentre;
    const rearIn = -tauCentre;
    const df = spec.diffs.front;
    const dr = spec.diffs.rear;
    const frontPower = frontIn * Math.sign(axle || 1) >= 0;
    const rearAxle = 0.5 * (wheelOmega[2] + wheelOmega[3]);
    const rearPower = rearIn * Math.sign(rearAxle || 1) >= 0;
    const lockF = (df.preload + (frontPower ? df.powerRamp : df.coastRamp) * Math.abs(frontIn)) * dt;
    const lockR = (dr.preload + (rearPower ? dr.powerRamp : dr.coastRamp) * Math.abs(rearIn)) * dt;
    act[2] = 1;
    J[2 * NB + 1] = 1;
    J[2 * NB + 2] = -1;
    lo[2] = -lockF;
    hi[2] = lockF;
    act[3] = 1;
    J[3 * NB + 3] = 1;
    J[3 * NB + 4] = -1;
    lo[3] = -lockR;
    hi[3] = lockR;
    // 4..7: brakes / rolling resistance
    for (let i = 0; i < 4; i++) {
      const k = 4 + i;
      act[k] = brakeLimit[i] > 0 ? 1 : 0;
      J[k * NB + 1 + i] = 1;
      lo[k] = -brakeLimit[i] * dt;
      hi[k] = brakeLimit[i] * dt;
    }

    // Warm start (clamped to the new limits).
    const lam = this.lambda;
    for (let k = 0; k < NC; k++) {
      if (!act[k]) {
        lam[k] = 0;
        continue;
      }
      lam[k] = clamp(lam[k] * 0.9, lo[k], hi[k]);
      const base = k * NB;
      for (let b = 0; b < NB; b++) om[b] += invI[b] * J[base + b] * lam[k];
    }
    // Iterate.
    for (let it = 0; it < 24; it++) {
      for (let k = 0; k < NC; k++) {
        if (!act[k]) continue;
        const base = k * NB;
        let jv = 0;
        let jmj = 0;
        for (let b = 0; b < NB; b++) {
          const j = J[base + b];
          if (j !== 0) {
            jv += j * om[b];
            jmj += j * j * invI[b];
          }
        }
        if (jmj <= 0) continue;
        let d = -jv / jmj;
        const old = lam[k];
        const nl = clamp(old + d, lo[k], hi[k]);
        d = nl - old;
        lam[k] = nl;
        if (d !== 0) for (let b = 0; b < NB; b++) om[b] += invI[b] * J[base + b] * d;
      }
    }

    // Engine can't run backwards; keep it above half idle as a safety net.
    const minOmega = spec.engine.idleRpm * 0.5 * RPM_TO_RADS;
    if (om[0] < minOmega) om[0] = minOmega;
    this.engineOmega = om[0];
    for (let i = 0; i < 4; i++) wheelOmega[i] = om[i + 1];
    this.out.clutchTorque = lam[0] / dt;
    this.out.centreTorque = lam[1] / dt;
    this.out.frontDiffTorque = lam[2] / dt;
    this.out.rearDiffTorque = lam[3] / dt;
  }

  /** Engine rpm the current gear would give at a given front-axle angular speed. */
  rpmAtAxle(gear: number, axleOmega: number): number {
    return Math.abs(this.ratio(gear) * axleOmega) * RADS_TO_RPM;
  }

  reset(): void {
    this.engineOmega = this.spec.engine.idleRpm * RPM_TO_RADS;
    this.gear = 1;
    this.targetGear = 1;
    this.shiftTimer = 0;
    this.shiftCooldown = 0;
    this.boost = 0;
    this.clutchEngage = 0;
    this.clutchLocked = false;
    this.lambda.fill(0);
    this.inReverse = false;
    this.reverseHold = 0;
  }
}
